import { DEFAULT_CAMERA_EVENT_TRIGGERS, EventTrigger, PREMIUM_CAMERA_EVENT_TRIGGERS } from '@cobliteam/common-types';
import useCameraFleetFlag from '@cobliteam/hooks/src/useCamera/useCameraFleetFlag';
import useImpactEvent from '@cobliteam/hooks/src/useImpactEvent';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useMediaEventLabel } from '@cobliteam/ui-business/utils/mediaUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface MultiSelectMediaEventTriggerProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectMediaEventTrigger = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectMediaEventTriggerProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);
  const { hasCameraProDMSEvents } = useCameraFleetFlag();
  const getMediaEventLabel = useMediaEventLabel();

  const isPremium = hasCameraProDMSEvents();

  const { isEventTriggerEnabled, videoImpactEvent } = useImpactEvent();

  const eventTriggersFilterOptions = useMemo(() => {
    const eventsToMap = [...DEFAULT_CAMERA_EVENT_TRIGGERS, ...(videoImpactEvent ? [EventTrigger.COLLISION] : []), ...(isPremium ? PREMIUM_CAMERA_EVENT_TRIGGERS : [])];
    return eventsToMap.filter(isEventTriggerEnabled).map((type) => ({
      key: type,
      label: getMediaEventLabel(type),
    }));
  }, [isPremium, getMediaEventLabel, isEventTriggerEnabled, videoImpactEvent]);

  // check if all selected types are in draft
  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = draft.find((d) => d === type);
    const updatedSelected = isSelected ? draft.filter((d) => d !== type) : [...draft, type];
    setDraft(updatedSelected as EventTrigger[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('media_event_trigger.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={eventTriggersFilterOptions}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.key} key={item.key} checked={draft.includes(item.key)} onCheckedChange={() => onChangeSelect(item.key)} />
                  <Label htmlFor={item.key}>{getMediaEventLabel(item.key as EventTrigger)}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('media_event_trigger.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('media_event_trigger.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='filter' />}>
        {t('media_event_trigger.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectMediaEventTrigger;
